import { useEffect, useState } from 'react';
import { api } from '../api/client';
import './MyRafflesPage.css';

interface Raffle {
  id: string;
  raffleId: string;
  prizeAmountUsdt: number;
  status: string;
  scheduledAt: string;
  createdAt: string;
  completedAt: string | null;
  participantCount: number;
  winnerTelegramId: string | null;
  winnerUsername: string | null;
  payoutStatus: string | null;
  language: string;
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; raffles: Raffle[] };

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
  expired: 'Expired',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#4caf50',
  completed: '#2196f3',
  cancelled: '#ff9800',
  failed: '#f44336',
  expired: '#9e9e9e',
};

function MyRafflesPage() {
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; raffleId: string | null }>({
    show: false,
    raffleId: null,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchRaffles = async () => {
    try {
      const result = await api.getMyRaffles();
      setState({ kind: 'loaded', raffles: result.raffles });
    } catch (error) {
      setState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Failed to load raffles',
      });
    }
  };

  useEffect(() => {
    fetchRaffles();
  }, []);

  const showCancelConfirm = (raffleId: string) => {
    setConfirmModal({ show: true, raffleId });
  };

  const handleCancelConfirmed = async () => {
    const raffleId = confirmModal.raffleId;
    if (!raffleId) return;

    setConfirmModal({ show: false, raffleId: null });
    setCancelling(raffleId);

    try {
      const result = await api.cancelMyRaffle(raffleId);
      if (result.success) {
        await fetchRaffles();
      } else {
        setErrorMessage(result.error || 'Failed to cancel raffle');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to cancel raffle');
    } finally {
      setCancelling(null);
    }
  };

  const handleCancelDismissed = () => {
    setConfirmModal({ show: false, raffleId: null });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (state.kind === 'loading') {
    return (
      <div className="my-raffles-page">
        <h1>My Raffles</h1>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="my-raffles-page">
        <h1>My Raffles</h1>
        <div className="error">{state.message}</div>
      </div>
    );
  }

  const { raffles } = state;

  if (raffles.length === 0) {
    return (
      <div className="my-raffles-page">
        <h1>My Raffles</h1>
        <div className="empty-state">
          <p>You haven't created any raffles yet.</p>
          <p className="hint">Use <code>@plzpaymebot raffle 100 +1d</code> in any Telegram chat to create a raffle.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-raffles-page">
      <h1>My Raffles</h1>

      <div className="raffles-list">
        {raffles.map((raffle) => (
          <div key={raffle.id} className="raffle-card">
            <div className="raffle-header">
              <span className="raffle-id">{raffle.raffleId}</span>
              <span
                className="raffle-status"
                style={{ backgroundColor: STATUS_COLORS[raffle.status] || '#9e9e9e' }}
              >
                {STATUS_LABELS[raffle.status] || raffle.status}
              </span>
            </div>

            <div className="raffle-details">
              <div className="raffle-row">
                <span className="label">Prize:</span>
                <span className="value">{raffle.prizeAmountUsdt} USDT</span>
              </div>
              <div className="raffle-row">
                <span className="label">Participants:</span>
                <span className="value">{raffle.participantCount}</span>
              </div>
              <div className="raffle-row">
                <span className="label">Scheduled:</span>
                <span className="value">{formatDate(raffle.scheduledAt)}</span>
              </div>
              <div className="raffle-row">
                <span className="label">Created:</span>
                <span className="value">{formatDate(raffle.createdAt)}</span>
              </div>
              {raffle.status === 'completed' && raffle.winnerTelegramId && (
                <div className="raffle-row winner-row">
                  <span className="label">🏆 Winner:</span>
                  <span className="value">
                    {raffle.winnerUsername ? `@${raffle.winnerUsername}` : `ID: ${raffle.winnerTelegramId}`}
                  </span>
                </div>
              )}
              {raffle.status === 'completed' && raffle.payoutStatus && (
                <div className="raffle-row">
                  <span className="label">Payout:</span>
                  <span className="value payout-status" data-status={raffle.payoutStatus}>
                    {raffle.payoutStatus === 'pending' && '⏳ Waiting for address'}
                    {raffle.payoutStatus === 'queued' && '📋 Queued'}
                    {raffle.payoutStatus === 'processing' && '⏳ Processing...'}
                    {raffle.payoutStatus === 'sent' && '✅ Sent'}
                    {raffle.payoutStatus === 'failed' && '❌ Failed'}
                  </span>
                </div>
              )}
              {raffle.status === 'completed' && !raffle.winnerTelegramId && raffle.participantCount === 0 && (
                <div className="raffle-row">
                  <span className="label">Result:</span>
                  <span className="value">😔 No participants - funds returned</span>
                </div>
              )}
            </div>

            {raffle.status === 'active' && (
              <div className="raffle-actions">
                {raffle.participantCount === 0 ? (
                  <button
                    className="cancel-btn"
                    onClick={() => showCancelConfirm(raffle.id)}
                    disabled={cancelling === raffle.id}
                  >
                    {cancelling === raffle.id ? 'Cancelling...' : 'Cancel Raffle'}
                  </button>
                ) : (
                  <div className="cancel-disabled-hint">
                    ⚠️ Cannot cancel - {raffle.participantCount} participant(s)
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Модальное окно подтверждения отмены */}
      {confirmModal.show && (
        <div className="modal-overlay" onClick={handleCancelDismissed}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Cancellation</h3>
            <p>Are you sure you want to cancel this raffle? Funds will be returned to your balance.</p>
            <div className="modal-buttons">
              <button className="btn-primary" onClick={handleCancelConfirmed}>
                Yes, Cancel Raffle
              </button>
              <button className="btn-secondary" onClick={handleCancelDismissed}>
                No, Keep Raffle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Сообщение об ошибке */}
      {errorMessage && (
        <div className="modal-overlay" onClick={() => setErrorMessage(null)}>
          <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()}>
            <h3>❌ Error</h3>
            <p>{errorMessage}</p>
            <div className="modal-buttons">
              <button className="btn-primary" onClick={() => setErrorMessage(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyRafflesPage;
